import chatttSDK from 'services/chattt';
import { savePendingAsaasPayment } from './pending';
import { registerAsaasReturnListener } from './returnListeners';
import {
  ASAAS_CURRENCY,
  ASAAS_PAY_TYPE,
  type AsaasCheckoutAttempt,
  type AsaasCheckoutOpenError,
  type AsaasCheckoutOpenResult,
  type AsaasOrderInfo,
  type AsaasPaymentSource,
} from './types';

interface CheckoutResponse {
  payType?: Pay.PayType;
  currency?: string;
  paymentUrl?: string;
}

export type AsaasStartResult<T extends AsaasOrderInfo> = {
  outcome: 'opened' | 'open-failed';
  order: T & {
    payType: typeof ASAAS_PAY_TYPE;
    paymentUrl: string;
    checkoutOpenError?: AsaasCheckoutOpenError;
  };
};

export function isHttpsPaymentUrl(value: string | null | undefined): value is string {
  if (!value) return false;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !!url.hostname;
  } catch {
    return false;
  }
}

export function resolveAsaasCheckoutUrl(response: CheckoutResponse): string {
  if (response.payType !== undefined && response.payType !== ASAAS_PAY_TYPE) {
    throw new Error(`unexpected payType for asaas order: ${response.payType}`);
  }
  if (response.currency !== undefined && response.currency !== ASAAS_CURRENCY) {
    throw new Error(`unexpected currency for asaas order: ${response.currency}`);
  }
  if (!isHttpsPaymentUrl(response.paymentUrl)) {
    throw new Error('asaas order returned invalid paymentUrl');
  }
  return response.paymentUrl;
}

export async function openAsaasCheckout(paymentUrl: string): Promise<AsaasCheckoutOpenResult> {
  if (!isHttpsPaymentUrl(paymentUrl)) return 'failed';
  try {
    if (chatttSDK?.openExternal) {
      await chatttSDK.openExternal(paymentUrl);
      return 'opened';
    }
    const opened = window.open(paymentUrl, '_blank');
    if (!opened) return 'blocked';
    opened.opener = null;
    return 'opened';
  } catch {
    return 'failed';
  }
}

export async function startAsaasCheckout<T extends AsaasOrderInfo>(
  order: T,
  response: CheckoutResponse,
  source: AsaasPaymentSource,
  attempt: AsaasCheckoutAttempt,
): Promise<AsaasStartResult<T>> {
  const paymentUrl = resolveAsaasCheckoutUrl(response);
  const saved = savePendingAsaasPayment(order, source, attempt.returnId);
  if (!saved.ok) throw new Error(`asaas pending snapshot save failed: ${saved.reason}`);
  registerAsaasReturnListener(attempt.returnId);
  const opened = await openAsaasCheckout(paymentUrl);
  return {
    outcome: opened === 'opened' ? 'opened' : 'open-failed',
    order: {
      ...order,
      payType: ASAAS_PAY_TYPE,
      paymentUrl,
      checkoutOpenError: opened === 'opened' ? undefined : opened,
    },
  };
}
